/**
 * @file 復習カードの取得・作成と編集・結果の記録・削除。
 *
 * @what: 家族の全カードを 1 回読み、操作は即 API (作成と編集は PUT、結果は POST、削除は
 *   DELETE)。返ってきたカードで一覧を差し替える。
 * @why: カードは「その場で 1 枚作る / 1 回答える」操作なので、plan の draft + 保存バーとは
 *   ライフサイクルを分ける (記録と同じ)。
 * @failure: 取得失敗は status "error"、操作の失敗は actionError に文言 (詳細は console)。
 */

import { useCallback, useEffect, useState } from "react";
import {
  deleteReviewCard,
  fetchReviewCards,
  postReviewResult,
  putReviewCard,
  type StudyReviewCard,
  type StudyReviewCardPutRequest,
  type StudyReviewResultKind,
} from "../api/reviewCards";

export type ReviewCardsState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; cards: StudyReviewCard[] };

export function useReviewCards(familyId: string, enabled = true) {
  const [state, setState] = useState<ReviewCardsState>({ status: "loading" });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setState({ status: "ready", cards: [] });
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchReviewCards(familyId, controller.signal)
      .then((cards) => {
        if (!controller.signal.aborted) setState({ status: "ready", cards });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load review cards", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, enabled]);

  const replace = (card: StudyReviewCard) =>
    setState((prev) => {
      if (prev.status !== "ready") return { status: "ready", cards: [card] };
      const at = prev.cards.findIndex((c) => c.id === card.id);
      if (at < 0) return { ...prev, cards: [card, ...prev.cards] };
      const cards = [...prev.cards];
      cards[at] = card;
      return { ...prev, cards };
    });

  const save = useCallback(
    async (cardId: string, input: StudyReviewCardPutRequest): Promise<StudyReviewCard | null> => {
      if (busy) return null;
      setBusy(true);
      setActionError(null);
      try {
        const card = await putReviewCard(familyId, cardId, input);
        replace(card);
        return card;
      } catch (err: unknown) {
        console.error("[study] failed to save review card", err);
        setActionError("保存できませんでした。時間を置いて再度お試しください。");
        return null;
      } finally {
        setBusy(false);
      }
    },
    [busy, familyId],
  );

  const answer = useCallback(
    async (cardId: string, result: StudyReviewResultKind, date?: string): Promise<boolean> => {
      if (busy) return false;
      setBusy(true);
      setActionError(null);
      try {
        const card = await postReviewResult(familyId, cardId, date ? { result, date } : { result });
        replace(card);
        return true;
      } catch (err: unknown) {
        console.error("[study] failed to record review result", err);
        setActionError("記録できませんでした。時間を置いて再度お試しください。");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [busy, familyId],
  );

  const remove = useCallback(
    async (cardId: string): Promise<void> => {
      if (busy) return;
      setBusy(true);
      setActionError(null);
      try {
        await deleteReviewCard(familyId, cardId);
        setState((prev) =>
          prev.status === "ready"
            ? { ...prev, cards: prev.cards.filter((c) => c.id !== cardId) }
            : prev,
        );
      } catch (err: unknown) {
        console.error("[study] failed to delete review card", err);
        setActionError("削除できませんでした。時間を置いて再度お試しください。");
      } finally {
        setBusy(false);
      }
    },
    [busy, familyId],
  );

  return { state, save, answer, remove, busy, actionError };
}
